import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import TextInput from '../../components/ui/TextInput.jsx';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import DateTimePicker from '../../components/ui/DateTimePicker';
import Button from '../../components/ui/Button';
import A11yDialog, { DialogErrorLine } from '../../components/ui/A11yDialog';
import { toUtcIso } from '../../utils/Utils';
import { employeeDisplayName } from '../ats-requisitions/employeeOption';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 10px;
  max-width: 560px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 28px 30px;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const Title = styled.h3`
  margin: 0 0 4px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Subtitle = styled.p`
  margin: 0 0 22px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-size: 13px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FullRow = styled.div`
  margin-bottom: 14px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
`;

const INTERVIEW_TYPES = ['phone', 'video', 'onsite', 'panel', 'technical', 'culture'];

// Build a "yyyy-MM-ddTHH:mm" stamp local to the browser, suitable for
// datetime-local inputs and JS Date parsing alike.
const toLocalDtString = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${mi}`;
};

export default function InterviewScheduleDialog({ open, application, onClose, onCreated }) {
  const { t } = useTranslation();

  const [form, setForm] = useState({
    interviewType: 'video',
    scheduledStart: '',
    scheduledEnd: '',
    location: '',
    meetingLink: '',
    participantIds: [],
    scorecardId: '',
  });
  const [submitting, setSubmitting] = useState(false);
  // EO-23 — mülakata bir DEĞERLENDİRME ŞABLONU çapalanabilir. Çapa konduğunda
  // panelist yalnız o şablonla puan verebilir; çapasız mülakatta tek
  // değerlendirici üç farklı şablonla üç satır yazıp aday ortalamasında üç kez
  // sayılabiliyordu. Alan İSTEĞE BAĞLIDIR (boş = eski davranış).
  const [templates, setTemplates] = useState([]);
  // FE-25 — "kirli form" kapısı: yalnız kullanıcının dokunduğu alanlar sayılır
  // (açılışta konan varsayılan saat aralığı kirlilik DEĞİLDİR).
  const [touched, setTouched] = useState(false);
  // FE-40 — istemci doğrulaması artık toast değil, diyaloğun İÇİNDE role="alert".
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Default to next hour boundary, 1-hour slot.
    const start = new Date();
    start.setMinutes(0, 0, 0);
    start.setHours(start.getHours() + 1);
    const end = new Date(start);
    end.setHours(end.getHours() + 1);
    setTouched(false);
    setFormError(null);
    setForm({
      interviewType: 'video',
      scheduledStart: toLocalDtString(start),
      scheduledEnd: toLocalDtString(end),
      location: '',
      meetingLink: '',
      participantIds: [],
      scorecardId: '',
    });
  }, [open, application?.id]);

  useEffect(() => {
    if (!open) return;
    api.get('/v1/ats-scorecards?pageSize=200&isActive[eq]=true')
      .then((res) => {
        // api.js GET'lerde reject ETMEZ — gerçek sinyal res.data.status.
        if (!res.data?.status) return;
        const result = res.data.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setTemplates(items.map((sc) => ({ id: sc.id, name: sc.name })));
      })
      .catch(() => {});
  }, [open]);

  const typeOptions = useMemo(() => INTERVIEW_TYPES.map((k) => ({
    id: k,
    name: t(`ats.interview.type.${k}`, { defaultValue: k }),
  })), [t]);

  // Mülakatçı seçici — ev içi çalışan seçici sözleşmesi (ats-requisitions/config
  // HM/HR BP seçicisiyle aynı): GET /v1/employees fullName[like], pageSize 20;
  // seçenek `profilePicture` taşır ki select kişiyi avatarlı çizsin
  // (ui/personOption). Kimlik Employee.Id — katılımcı satırının UserId'si
  // sunucuda kimlik ailesiyle çözülür. Sabit referans şart: AsyncGenericSelect
  // debounce'unu `onSearch` üzerinden memo'lar.
  const searchEmployees = useCallback(async (q) => {
    const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    const r = res.data?.data;
    const items = Array.isArray(r) ? r : (r?.data || []);
    return items.map((e) => ({ id: e.id, name: employeeDisplayName(e), profilePicture: e.profilePicture }));
  }, []);

  const setField = (k, v) => {
    setTouched(true);
    setFormError(null);
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const handleSubmit = async () => {
    if (!application?.id) return;
    setFormError(null);
    if (!form.scheduledStart || !form.scheduledEnd) {
      setFormError(t('ats.interview.schedule.timeRequired'));
      return;
    }
    if (new Date(form.scheduledEnd) <= new Date(form.scheduledStart)) {
      setFormError(t('ats.interview.schedule.endBeforeStart'));
      return;
    }
    // Mülakat, başvurudan önce olamaz — sunucu da reddeder; burada anında
    // geri bildirim veriyoruz.
    if (application?.appliedAt && new Date(form.scheduledStart) < new Date(application.appliedAt)) {
      setFormError(t('responseErrors.ats.interview.beforeApplication'));
      return;
    }
    // Katılımcısız mülakat, mülakatçıya ulaşılamayan mülakattır (skorkart kapısı
    // katılımcılığa bakar) — en az bir mülakatçı zorunlu.
    if (!Array.isArray(form.participantIds) || form.participantIds.length === 0) {
      setFormError(t('ats.interview.schedule.participantsRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        applicationId: application.id,
        // Send absolute UTC — offset-less form values would otherwise be stored
        // hours off in non-UTC timezones (see toUtcIso).
        scheduledStart: toUtcIso(form.scheduledStart),
        scheduledEnd: toUtcIso(form.scheduledEnd),
        interviewType: form.interviewType || 'video',
        location: form.location || null,
        meetingLink: form.meetingLink || null,
        scorecardId: form.scorecardId || null,
        participants: form.participantIds.map((userId) => ({ userId, role: 'peer', isRequired: true })),
      };
      const res = await api.post('/v1/ats-interviews', payload);
      if (res.data?.status) {
        onCreated?.(res.data.data);
        onClose?.();
      }
    } catch {
      // interceptor toasts
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <A11yDialog
      open={open}
      onClose={onClose}
      dirty={touched}
      overlay={Overlay}
      card={Card}
      overlayProps={{ $open: true }}
    >
      {({ titleId }) => (
      <>
        <Title id={titleId}>{t('ats.interview.schedule.title')}</Title>
        <Subtitle>
          {application?.candidateFullName || ''}
          {application?.jobPostingTitle ? ` — ${application.jobPostingTitle}` : ''}
        </Subtitle>

        <FullRow>
          <GenericSelect
            label={t('ats.interview.typeLabel')}
            options={typeOptions}
            value={form.interviewType}
            onChange={(id) => setField('interviewType', id || 'video')}
            required
          />
        </FullRow>

        <Grid>
          <DateTimePicker
            label={t('ats.interview.scheduledStart')}
            mode="datetime"
            value={form.scheduledStart}
            minDate={application?.appliedAt}
            onChange={(v) => setField('scheduledStart', v || '')}
          />
          <DateTimePicker
            label={t('ats.interview.scheduledEnd')}
            mode="datetime"
            value={form.scheduledEnd}
            onChange={(v) => setField('scheduledEnd', v || '')}
          />
        </Grid>

        <FullRow>
          <AsyncGenericSelect
            label={t('ats.interview.schedule.participantsLabel')}
            isMulti
            value={form.participantIds}
            onChange={(ids) => setField('participantIds', Array.isArray(ids) ? ids : [])}
            onSearch={searchEmployees}
            required
          />
        </FullRow>

        <FullRow>
          <GenericSelect
            label={t('ats.interview.scorecardTemplate')}
            options={templates}
            value={form.scorecardId}
            onChange={(id) => setField('scorecardId', id || '')}
            placeholder={t('ats.interview.scorecardTemplateOptional')}
          />
        </FullRow>

        <FullRow>
          <TextInput
            label={t('ats.interview.location')}
            value={form.location}
            onChange={(e) => setField('location', e.target.value)}
          />
        </FullRow>

        <FullRow>
          <TextInput
            label={t('ats.interview.meetingLink')}
            value={form.meetingLink}
            onChange={(e) => setField('meetingLink', e.target.value)}
          />
        </FullRow>

        {formError && <DialogErrorLine role="alert" data-testid="interview-form-error">{formError}</DialogErrorLine>}

        <Actions>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} loading={submitting} disabled={submitting}>
            {t('ats.interview.schedule.submit')}
          </Button>
        </Actions>
      </>
      )}
    </A11yDialog>
  );
}
